import React, { useEffect, useState, useRef } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  PropertyContextProvider,
  usePropertyContext,
} from "../context/property-context-v2";
import SearchBar from "./SearchBar";
import Listings from "./Listings-v2";
import Map from "./Map-v2";
import BottomBar from "./BottomBar";
import "../styles/searchbar.css";
import HomeTitle from "./HomeTitle";

const queryClient = new QueryClient();

const SearchPageContent = () => {
  const [center, setCenter] = useState<[number, number] | null>(null);
  const [place, setPlace] = useState<{ city: string; state: string } | null>(
    null,
  );
  const [mapVisible, setMapVisibility] = useState(true);
  const { setFilters } = usePropertyContext();
  const lastSavedSearch = useRef<string | null>(null);

  const onPlaceSelected = ({
    lat,
    lng,
    city,
    state,
  }: {
    lat: string;
    lng: string;
    city: string;
    state: string;
  }) => {
    createURL({ lat, lng, city, state });
    setCenter([parseFloat(lng), parseFloat(lat)]);
    setPlace({ city, state });
  };

  const createURL = ({
    lat,
    lng,
    city,
    state,
  }: {
    lat: string;
    lng: string;
    city: string;
    state: string;
  }) => {
    const baseUrl = window.location.href.split("?")[0];
    const formattedUrl = `${baseUrl}?lat=${lat}&lng=${lng}&state=${state}&city=${city}`;
    window.history.pushState({}, "", formattedUrl);
  };

  const updateFromURL = () => {
    try {
      const url = new URL(window.location.href.toLowerCase());
      const lat = url.searchParams.get("lat");
      const lng = url.searchParams.get("lng");
      const city = url.searchParams.get("city");
      const state = url.searchParams.get("state");

      if (lat && lng) {
        setCenter([parseFloat(lng), parseFloat(lat)]);
      } else {
        setCenter(null);
      }

      if (city && state) {
        setPlace({ city, state });
      }

      const filtersFromUrl: Record<string, string> = {};

      const filterKeys = [
        "bedrooms",
        "bathrooms",
        "sqft_low",
        "sqft_high",
        "year_built_low",
        "year_built_high",
        "price_low",
        "price_high",
        "min_beds",
        "max_beds",
        "min_baths",
        "max_baths",
        "zoom",
      ];

      filterKeys.forEach((key) => {
        const value = url.searchParams.get(key);
        if (value && value.trim() !== "") {
          filtersFromUrl[key] = value.trim();
        }
      });

      if (Object.keys(filtersFromUrl).length > 0 && setFilters) {
        setFilters((prevFilters: any) => ({
          ...prevFilters,
          ...filtersFromUrl,
        }));
      }
    } catch (error) {
      console.error("Error parsing URL:", error);
    }
  };

  useEffect(() => {
    updateFromURL();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (window.location.pathname === "/search") {
        window.location.reload();
      } else {
        updateFromURL();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href.toLowerCase());
    const lat = url.searchParams.get("lat");
    const lng = url.searchParams.get("lng");
    const city = url.searchParams.get("city");
    const state = url.searchParams.get("state");

    if (lat && lng && city && state) {
      const searchKey = `${lat}-${lng}-${city}-${state}`;

      // Only save if this is a different search
      if (lastSavedSearch.current !== searchKey) {
        lastSavedSearch.current = searchKey;

        // Track search for analytics
        fetch("/search/track", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('[name="csrf-token"]')
                ?.getAttribute("content") || "",
          },
          body: JSON.stringify({ query: { lat, lng, city, state } }),
        }).catch((error) => console.error("Error tracking search", error));

        // Save to search history for dashboard
        fetch("/search_history/log_search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('[name="csrf-token"]')
                ?.getAttribute("content") || "",
          },
          body: JSON.stringify({
            query: JSON.stringify({ lat, lng, city, state }),
            name: `${city}, ${state}`,
          }),
        }).catch((error) =>
          console.error("Error saving search history", error),
        );
      }
    }
  }, [center, place]);

  return (
    <>
      <div className="sticky pt-5 top-20 bg-white z-30">
        <SearchBar
          onPlaceSelected={onPlaceSelected}
          place={place}
          mapVisible={mapVisible}
          toggleMapVisibility={() => setMapVisibility(!mapVisible)}
        />
      </div>
      <div className="relative sm:px-0">
        <HomeTitle />
        <div className="flex relative rounded-lg xl:pl-4 h-[calc(100vh-227px)]">
          <Listings visible={!mapVisible} />
          <Map visible={mapVisible} center={center} />
        </div>
        <BottomBar />
      </div>
      <ToastContainer />
    </>
  );
};

export const SearchPageV2 = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <PropertyContextProvider>
        <SearchPageContent />
      </PropertyContextProvider>
    </QueryClientProvider>
  );
};

export default SearchPageV2;
